import type { EChartsOption } from 'echarts';

import { buildAnimationConfig } from '../shared/animation';
import { buildTooltipConfig, buildStackedSwatchBody } from '../shared/tooltip';
import { buildPieSeries } from './series';
import type { PieChartConfig, PieChartDatum } from './types';

type TooltipParam = { dataIndex?: number };

// Shared by entrance and update transitions so a first load animates at the same speed as a
// later data refresh.
const PIE_ANIMATION_DURATION_MS = 300;

// Per-slice tooltip: a color dot beside the category name and its signed amount, stacked. Matches
// the amcharts `tooltipHTML` layout. The amount is privacy-masked exactly as amcharts did.
const buildPieTooltipFormatter = (data: PieChartDatum[], config: PieChartConfig) => {
  const { theme, privacyMask } = config;

  return (params: unknown): string => {
    const p = (Array.isArray(params) ? params[0] : params) as TooltipParam;
    const datum = typeof p?.dataIndex === 'number' ? data[p.dataIndex] : undefined;
    if (!datum) return '';
    if (datum.tooltipHtml) return datum.tooltipHtml;

    const amount = String(privacyMask(datum.tooltipAmount));
    return buildStackedSwatchBody(datum.color, datum.name, amount, theme);
  };
};

export const buildPieChartOption = (data: PieChartDatum[], config: PieChartConfig): EChartsOption => {
  const { theme, animated = true } = config;
  const hasCustomTooltipHtml = data.some((datum) => datum.tooltipHtml);

  return {
    ...buildAnimationConfig(animated),
    // Overrides shared/animation.ts's 600ms entrance default.
    animationDuration: animated ? PIE_ANIMATION_DURATION_MS : 0,
    animationDurationUpdate: animated ? PIE_ANIMATION_DURATION_MS : 0,
    animationEasingUpdate: 'cubicOut',
    textStyle: { fontFamily: theme.fontFamily },
    tooltip: {
      ...buildTooltipConfig(theme),
      // Rich templates render their own background, border, and padding. Removing the ECharts
      // shell prevents a second outlined container around the custom tooltip.
      ...(hasCustomTooltipHtml
        ? { backgroundColor: 'transparent', borderWidth: 0, padding: 0, extraCssText: 'box-shadow: none;' }
        : {}),
      // Pie has no axis — the tooltip fires per slice, not per category column.
      trigger: 'item',
      formatter: buildPieTooltipFormatter(data, config),
    },
    series: [buildPieSeries(data, config)],
  };
};
